import type { ConversationMessage, EditPayload, AttachmentRef, FulltextFileRef, SearchSource } from "@lupp/shared";
import type { ClientToServer, ServerToClient } from "@lupp/shared";
import { getSocket } from "./socket";

export interface AssistantRequest {
  model: string;
  systemPrompt: string;
  history: ConversationMessage[];
  userMessage: string;
  chatId?: string; // OWUI-Chat der laufenden Konversation; fehlt ⇒ Server legt neuen an
  onChatCreated?: (chatId: string) => void; // Server-vergebene chatId des neuen Chats
  previousResponseId?: string;
  signal?: AbortSignal;
  reasoningEffort?: "low" | "medium" | "high";
  onThinking?: (update: ThinkingUpdate) => void;
  onMessageDelta?: (textSoFar: string) => void;
  onChangeProposals?: (payload: EditPayload) => void;
  appliedChangeIds?: string[];
  rejectedChangeIds?: string[];
  attachments?: AttachmentRef[];
  fulltextFiles?: FulltextFileRef[];
  webSearch?: boolean;
  parentMessageId?: string | null; // selected branch leaf to append under; null = fork at the root
  folderId?: string; // create a new chat inside this project (folder)
  onSources?: (sources: SearchSource[]) => void;
}

export interface ThinkingUpdate {
  type: "reasoning" | "tool_call" | "tool_result";
  text: string;
}

export interface AssistantResponse {
  text: string;
  thinkingSteps: string[];
  sources: never[];
  responseId?: string;
}

function generateTurnId(): string {
  return `turn-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`;
}

function previewText(s: string): string {
  return s.length > 120 ? `${s.slice(0, 120)}…` : s;
}

export async function sendMessage(req: AssistantRequest): Promise<AssistantResponse> {
  const socket = getSocket();
  const turnId = generateTurnId();
  const thinkingSteps: string[] = [];
  let finalText = "";
  let responseId: string | undefined;

  // LO-48 item-lifecycle protocol + LO-41 model selection: the client picks the
  // OWUI model (+ a context system-prompt suffix); the server resolves it against
  // the OWUI registry (LiteLLM model / base prompt / reasoning effort). Multi-turn
  // continuity rides on previousResponseId (R-6a); history is the fallback when
  // there is no responseId to chain from.
  const userTurn: ClientToServer = {
    type: "user_turn",
    turnId,
    text: req.userMessage,
    model: req.model,
    systemPrompt: req.systemPrompt,
    ...(req.chatId ? { chatId: req.chatId } : {}),
    previousResponseId: req.previousResponseId,
    history: req.previousResponseId
      ? undefined
      : req.history
          .filter((m) => !m.isStreaming && m.content.trim())
          .map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
    // Accept/Reject decisions from the previous turn ride along (R-21a).
    ...(req.appliedChangeIds ? { appliedChangeIds: req.appliedChangeIds } : {}),
    ...(req.rejectedChangeIds ? { rejectedChangeIds: req.rejectedChangeIds } : {}),
    ...(req.attachments?.length ? { attachments: req.attachments } : {}),
    ...(req.fulltextFiles?.length ? { fulltextFiles: req.fulltextFiles } : {}),
    ...(req.webSearch ? { webSearch: true } : {}),
    ...(req.parentMessageId !== undefined ? { parentMessageId: req.parentMessageId } : {}),
    ...(req.folderId ? { folderId: req.folderId } : {}),
  };

  return new Promise<AssistantResponse>((resolve, reject) => {
    // Wire up abort before emitting, so we don't miss a synchronous abort.
    const onAbort = (): void => {
      const abortPayload: ClientToServer = { type: "abort", turnId };
      socket.emit("abort", abortPayload);
    };
    req.signal?.addEventListener("abort", onAbort);

    const cleanup = (): void => {
      socket.off("event", onEvent);
      req.signal?.removeEventListener("abort", onAbort);
    };

    // LO-48 item-lifecycle: the committed OutputItem is the truth. item_delta is an
    // optimistic streaming preview we don't surface yet; finalText comes from the
    // committed message item, responseId from turn_end.
    let messageItemId: string | undefined;
    let streamingText = "";
    const onEvent = (event: ServerToClient): void => {
      switch (event.type) {
        case "item_start": {
          // Track the message item so we can surface its streamed deltas live.
          if (event.kind === "message") messageItemId = event.itemId;
          break;
        }
        case "item_delta": {
          if (event.itemId === messageItemId) {
            streamingText += event.delta;
            req.onMessageDelta?.(streamingText);
          }
          break;
        }
        case "item_commit": {
          const item = event.item;
          switch (item.type) {
            case "reasoning": {
              const text = item.summary.map((s) => s.text).join("\n");
              thinkingSteps.push(text);
              req.onThinking?.({ type: "reasoning", text });
              break;
            }
            case "function_call": {
              const text = `${item.name}(${item.arguments})`;
              thinkingSteps.push(text);
              req.onThinking?.({ type: "tool_call", text });
              break;
            }
            case "function_call_output": {
              const text = `↩ ${previewText(item.output)}`;
              thinkingSteps.push(text);
              req.onThinking?.({ type: "tool_result", text });
              break;
            }
            case "message": {
              finalText = item.content
                .filter((c) => c.type === "output_text")
                .map((c) => c.text)
                .join("");
              break;
            }
          }
          break;
        }
        // change_proposals is a sibling event (not an output[] item), correlated
        // to the edit function_call by callId (R-18a); the client applies it
        // AI-independently via the PRD-0003 engine.
        case "chat_created": {
          // Server hat für diese Konversation einen OWUI-Chat angelegt; merken,
          // damit Folge-Turns dieselbe chatId senden (sonst je Turn ein neuer Chat).
          req.onChatCreated?.(event.chatId);
          break;
        }
        case "change_proposals": {
          req.onChangeProposals?.(event.payload);
          break;
        }
        case "sources": {
          // Citations → flat SearchSource[] for live display. Web sources carry a url;
          // knowledge sources have none (url:"") but a fileId/hash + name instead.
          req.onSources?.(
            event.sources.map((c) => ({
              title: c.sourceName || c.url || "",
              url: c.url ?? "",
              snippet: c.snippet,
              ...(c.fileId ? { fileId: c.fileId } : {}),
              ...(c.hash ? { hash: c.hash } : {}),
            })),
          );
          break;
        }
        case "turn_end": {
          if (event.status === "aborted") {
            cleanup();
            reject(new DOMException("Abgebrochen.", "AbortError"));
            break;
          }
          responseId = event.responseId;
          cleanup();
          resolve({ text: finalText, thinkingSteps, sources: [], responseId });
          break;
        }
        case "error": {
          cleanup();
          reject(new Error(event.message));
          break;
        }
      }
    };

    socket.on("event", onEvent);
    socket.emit("user_turn", userTurn);
  });
}
